import { useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { Button } from "@common/index";
import { ComponentColor } from "@common/types";
import type { LocationResponse } from "@models/location";
import { useLocationApi } from "@/hooks/apiHooks/useLocationApi";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import {
    certificateFileError,
    CERTIFICATE_REQUIRED_MESSAGE,
} from "@utils/certificateFile";

interface LocationResubmitFormProps {
    location: LocationResponse;
    onResubmitted: (locationId: string) => void;
}

// A rejected location's in-place certificate re-upload (#198 / E5-S11). Shared by
// the pending wall's resubmit card and the Account › Locations row (OB-09), which
// is why it lives here rather than in either feature. A removed location gets a
// line saying why there is no form: the API 404s its resubmit. Callers still
// show the rejection line above it.
export const LocationResubmitForm = ({
    location,
    onResubmitted,
}: LocationResubmitFormProps) => {
    const { resubmitLocation } = useLocationApi();
    const runGuarded = useSubmitGuard();

    const [file, setFile] = useState<File | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    // This form's own request only. The shared isProcessingLocationsAtom is
    // raised by every location mutation on the page, so reading it would spin
    // every row's button for one row's resubmit, or for an edit elsewhere.
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!location.isActive) {
        return (
            <p className="mt-2 text-sm text-neutral/70">
                This location was removed, so it can&apos;t be resubmitted.
            </p>
        );
    }

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        setFile(event.target.files?.[0] ?? null);
        setValidationError(null);
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!file) {
            setValidationError(CERTIFICATE_REQUIRED_MESSAGE);
            return;
        }

        const fileError = certificateFileError(file);
        if (fileError) {
            setValidationError(fileError);
            return;
        }

        // Guarded: `isSubmitting` disables the button a render late, so two
        // presses in one tick would otherwise send two POSTs, and the second
        // one's 409 toast would sit beside the first one's success.
        void runGuarded(async () => {
            setIsSubmitting(true);

            try {
                await resubmitLocation(location.id, file);
                onResubmitted(location.id);
            } catch {
                // useApi already surfaced the error toast; keep the form so
                // the user can retry.
            } finally {
                setIsSubmitting(false);
            }
        });
    };

    return (
        <form onSubmit={handleSubmit} noValidate className="mt-4">
            <label
                htmlFor={`resubmit-certificate-${location.id}`}
                className="block font-bold mb-2"
            >
                Upload Resale Certificate
            </label>
            <input
                id={`resubmit-certificate-${location.id}`}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="file-input file-input-bordered w-full bg-white text-neutral"
                onChange={handleFileChange}
            />
            <p className="text-xs text-neutral/60 mt-1">
                PDF, JPG, or PNG, up to 10 MB.
            </p>
            {validationError && (
                <p className="text-red-500 text-sm mt-1">{validationError}</p>
            )}
            <div className="mt-4">
                <Button
                    type="submit"
                    color={ComponentColor.Primary}
                    loading={isSubmitting}
                    className="normal-case"
                >
                    Resubmit
                </Button>
            </div>
        </form>
    );
};
